import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useQuery } from 'react-query';
import datasuiteApi from '../../api/datasuiteApi';
import LocalStorage from '../LocalStorage/LocalStorage';
import filterSetsApi from '../../api/filterSetsApi';
import useManufacturerData from '../useManufacturerData/useManufacturerData';
import reportingApi from '../../api/reportingApi';
import { isUsaIncluded } from '../../utils/countryUtils';
import { setAppliedUsaIncluded } from '../../reducers/DashboardReducer/DashboardActions';

const useFiltersWidget = () => {
    const dispatch = useDispatch();
    const portalBrand = localStorage.getItem('branding') || '';
    const portalType = portalBrand === 'ARROWSTREAM' ? 'SalesStream' : 'BEP';
    const { manufacturerParams } = useManufacturerData();
    const chainData = LocalStorage.userData.get()?.user?.chainData;
    const emergingData = LocalStorage.userData.get()?.user?.emergingData;
    const availableDatasets = () => {
        const datasets = [];
        if(chainData) datasets.push({value: "Chain Data", label: "Chain Data"});
        if(emergingData) datasets.push({value: "Emerging Chain Data", label: "Emerging Chain Data"});
        return datasets;
    };
    const getSelectedDatasetOptions = (filters = {}) => {
        const datasets = [];

        if (filters?.chainData) {
            datasets.push({ value: 'Chain Data', label: 'Chain Data' });
        }

        if (filters?.emergingData) {
            datasets.push({ value: 'Emerging Chain Data', label: 'Emerging Chain Data' });
        }

        return datasets;
    };
    const [selectedCategory, setSelectedCategory] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState([]);
    const [selectedParentLocation, setSelectedParentLocation] = useState([]);
    const [selectedChildLocation, setSelectedChildLocation] = useState([]);
    const [selectedSegment, setSelectedSegments] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedStates, setSelectedStates] = useState([]);
    const [selectedDatasets, setSelectedDatasets] = useState(availableDatasets());
    const [selectedFilterSet, setSelectedFilterSet] = useState(null);

    const getSelectedFilters = () => {
        return {
        countries: selectedCountries?.map(c => c.value) || [],
        states: selectedStates?.map(s => s.value) || [],
        segments: selectedSegment?.map(s => s.value) || [],
        products: selectedProduct?.map(p => p.value) || [],
        children: selectedChildLocation?.map(c => c.value) || [],
        locations: selectedParentLocation?.map(p => p.value) || [],
        categories: selectedCategory?.map(c => c.value) || [],
        chainData: selectedDatasets?.some(d => d.value === "Chain Data") ? true : false,
        emergingData: selectedDatasets?.some(d => d.value === "Emerging Chain Data") ? true : false
        };
    };

    const getAllFilters = () => {
        const params = {
            chainData: chainData,
            emergingData: emergingData,
            ...manufacturerParams
        };
        return datasuiteApi.filters.post(params).then((response) => {
            if (!isUsaIncluded(response?.data?.countries)) {
                dispatch(setAppliedUsaIncluded(false));
            }
            return response;
        }).catch(err => {
            console.log(err);
        });
    };
    const useAllFilters = () => useQuery({
        queryKey: ['allFilters'],
        queryFn: () => getAllFilters().then(r => r.data)
    });
    const [fromDate, setFromDate] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
    const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
    const minDate = moment().subtract(5, 'years').format('YYYY-MM-DD');
    const [originalFromDate, setOriginalFromDate] = useState(fromDate);
    const [originalToDate, setOriginalToDate] = useState(toDate);
    const hasFiltersSelected = () => {
        return (
            (selectedCountries && selectedCountries.length > 0) ||
            (selectedStates && selectedStates.length > 0) ||
            (selectedSegment && selectedSegment.length > 0) ||
            (selectedChildLocation && selectedChildLocation.length > 0) ||
            (selectedParentLocation && selectedParentLocation.length > 0) ||
            (selectedCategory && selectedCategory.length > 0) ||
            (selectedDatasets.length !== availableDatasets().length) ||
            (selectedProduct && selectedProduct.length > 0) ||
            (fromDate !== originalFromDate) ||
            (toDate !== originalToDate)
        );
    };
    const { data: allFilters, isLoading: filterLoading } = useAllFilters();
    const getChildrenFilters = () => {
        if(selectedParentLocation.length === 0) return Promise.resolve({data: []});
        const params = {
            chainData,
            emergingData,
            parents: selectedParentLocation?.map(l => l.value) || []
        }
        return datasuiteApi.filters.children.post(params).then(r => r);
    };
    const useChildrenFilters = () => useQuery({
        queryKey: ['childrenFilters', selectedParentLocation],
        queryFn: () => getChildrenFilters().then(r => r.data)
    });
    const { data: childrenFilters, isLoading: childrenLoading } = useChildrenFilters();
    const getReportFilters = () => {
        const params = {
            portal: portalType,
            ...manufacturerParams
        }
        return reportingApi.getReportFilters.post(params);
    };
    const useReportsFilters = () => useQuery({
        queryKey: ['reportsFilters'],
        queryFn: () => getReportFilters().then(r => r.data)
    });
    const { data: reportFilters, isLoading: reportFiltersLoading } = useReportsFilters();
    const getSavedFilterSets = () => {
        return filterSetsApi.get().then(r => r.data).catch(err => {
            console.log('Error fetching saved filter sets: ', err);
            return [];
        });
    };
    const useSavedFilterSets = () => useQuery({
        queryKey: ['savedFilterSets'],
        queryFn: () => getSavedFilterSets().then(r => r)
    });
    const { data: savedFilterSets, isLoading: savedFilterSetsLoading, refetch: refetchSavedFilterSets } = useSavedFilterSets();
    const datasetOptions = [
        {
            value: "Chain Data",
            label: "Chain Data",
            isDisabled: !chainData
        },
        {
            value: "Emerging Chain Data",
            label: "Emerging Chain Data",
            isDisabled: !emergingData
        }
    ];
    const saveFilterSet = (name, filters) => {
        const params = {
            name,
            isDefault: false,
            filters: filters
        };
        filterSetsApi.post(params).then(r => r.data).then(() => {
            refetchSavedFilterSets();
        });
    };

    const setFiltersFromFilterSet = () => {
        if(!selectedFilterSet) {
            clearAllFilters();
            return;
        };
        const filters = savedFilterSets?.find(set => set.id === selectedFilterSet.value)?.filters;
        if(!filters) return;
        setSelectedCategory(filters?.categories?.map(c => ({value: c, label: c})) || []);
        setSelectedProduct(filters?.products?.map(p => ({value: p, label: p})) || []);
        setSelectedParentLocation(filters?.locations?.map(l => ({value: l, label: l})) || []);
        setSelectedChildLocation(filters?.children?.map(l => ({value: l, label: l})) || []);
        setSelectedSegments(filters?.segments?.map(s => ({value: s, label: s})) || []);
        setSelectedCountries(filters?.countries?.map(c => ({value: c, label: c})) || []);
        setSelectedStates(filters?.states?.map(s => ({value: s, label: s})) || []);
        setSelectedDatasets(getSelectedDatasetOptions(filters));
    };

    const clearAllFilters = (datePickerRef = null) => {
        setSelectedCountries([]);
        setSelectedStates([]);
        setSelectedCategory([]);
        setSelectedProduct([]);
        setSelectedParentLocation([]);
        setSelectedChildLocation([]);
        setSelectedSegments([]);
        setSelectedFilterSet(null);
        setSelectedDatasets(availableDatasets());
        setFromDate(originalFromDate);
        setToDate(originalToDate);
        if (datePickerRef && datePickerRef.current) {
            datePickerRef.current.setStartDate(moment(originalFromDate));
            datePickerRef.current.setEndDate(moment(originalToDate));
        }
    };

    const deleteFilterSet = (id) => {
        filterSetsApi.delete(id).then(() => {
            if(selectedFilterSet?.value === id) {
                setSelectedFilterSet(null);
            }
            refetchSavedFilterSets();
        }).catch(err => {
            console.log('Error deleting filter set: ', err);
        });
    };

    const renameFilterSet = (id, newName) => {
        filterSetsApi.patch(id, { name: newName }).then(() => {
            refetchSavedFilterSets().then(() => {
                setSelectedFilterSet({ value: id, label: newName });
            });
        }).catch(err => {
            console.log('Error renaming filter set: ', err);
        });
    };

    const setFilterSetAsDefault = (id) => {
        filterSetsApi.patch(id, { isDefault: true }).then(() => {
            refetchSavedFilterSets();
        }).catch(err => {
            console.log('Error setting filter set as default: ', err);
        });
    };

    const updateFilterSet = (id, name, filters) => {
        return filterSetsApi.patch(id, { name, filters }).then(() => {
            return refetchSavedFilterSets();
        }).catch(err => {
            console.log('Error updating filter set: ', err);
            throw err;
        });
    };

    useEffect(() => {
        setFiltersFromFilterSet();
        // eslint-disable-next-line
    }, [selectedFilterSet]);

    return {
        selectedCategory, setSelectedCategory,
        selectedParentLocation, setSelectedParentLocation,
        selectedChildLocation, setSelectedChildLocation,
        selectedSegment, setSelectedSegments,
        selectedCountries, setSelectedCountries,
        selectedStates, setSelectedStates,
        selectedFilterSet, setSelectedFilterSet,
        selectedProduct, setSelectedProduct,
        selectedDatasets, setSelectedDatasets,
        fromDate, setFromDate,
        toDate, setToDate,
        originalFromDate, setOriginalFromDate,
        originalToDate, setOriginalToDate,
        minDate,
        hasFiltersSelected,
        getSelectedFilters,
        allFilters,
        filterLoading,
        reportFilters,
        reportFiltersLoading,
        childrenFilters,
        childrenLoading,
        savedFilterSets,
        savedFilterSetsLoading,
        datasetOptions,
        clearAllFilters,
        saveFilterSet,
        updateFilterSet,
        deleteFilterSet,
        renameFilterSet,
        setFilterSetAsDefault,
        emergingData,
        chainData
    };
}
export default useFiltersWidget;
